/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { TreeNode } from './tree-node';
import { Subject, Subscription } from 'rxjs';
import { FileColumn } from './file-column';

export abstract class TreeManager {
  public labels: string[];
  protected loader: Subject<TreeNode>;
  public columns: FileColumn[] = [];
  public currentColumn: FileColumn;
  public historic: FileColumn[];
  protected root: TreeNode;
  protected isFiltered: boolean;
  public crumbs: FileColumn[] = [];
  public isCollapsed = false;

  protected constructor(labels?: string[]) {
    this.labels = labels;
  }

  public subscribe(
    next: (nodes: TreeNode) => void,
    error?: (error) => void,
  ): Subscription {
    return this.loader.subscribe({ next, error });
  }

  public addColumn(node: TreeNode, collapse?: boolean) {
    if (node) {
      this.historic = null;
      if (node.nodes && node.nodes.length > 0) {
        let column: FileColumn;
        if (this.labels && this.columns.length - 1 < this.labels.length) {
          column = new FileColumn(
            this.columns.length - 1 + '',
            this.labels[this.columns.length - 1],
            node,
            this.isFiltered,
          );
        } else {
          column = new FileColumn(
            this.columns.length - 1 + '',
            node.label,
            node,
            this.isFiltered,
          );
        }
        column.collapse(collapse);
        this.columns.push(column);
        this.crumbs.push(column);
      }
    }
  }

  public manageColumns(index: number, node: TreeNode, collapse?: boolean) {
    if (index + 1 >= this.columns.length) {
      this.addColumn(node, collapse);
    } else {
      this.columns = this.columns.slice(0, index + 1);
      this.crumbs = this.columns.slice(0);
      if (node) {
        this.addColumn(node, collapse);
      }
    }
    // this.manageBreadCrumbs(index, node);
  }

  public getParent(): TreeNode {
    if (this.columns.length > 1) {
      return this.columns[this.columns.length - 2].node;
    } else {
      return null;
    }
  }

  public getValue(index: number): TreeNode {
    if (index >= 0 && index < this.columns.length) {
      return this.columns[index].node;
    } else {
      return null;
    }
  }

  public goBack() {
    const index = this.columns.indexOf(this.currentColumn);
    if (index >= 0) {
      this.manageColumns(index, null);
      this.columns[this.columns.length - 1].selectedNode = null;
      this.currentColumn = this.columns[index - 1];
    }
  }

  public filter(value: string) {
    if (value) {
      this.isFiltered = true;
      this.root.filter(value);
    } else {
      this.isFiltered = false;
    }
    this.columns = [];
    this.addColumn(this.root);
  }

  public goTo(index: number) {
    if (!this.historic) {
      this.historic = this.columns.slice(0);
    }
    if (index >= 0) {
      this.manageColumns(index, null);
      // this.manageBreadCrumbs(index, null);
    }
  }

  public reload() {
    if (this.historic) {
      this.columns = this.historic;
    }
  }

  public findBreathCrumb(): TreeNode[] {
    return this.columns.map((n) => n.selectedNode);
  }

  /**
   * Refresh column content on define position
   * @param position if undefined, refresh the last column
   */
  public refreshColumn(position?: number) {
    if (position && position > 0 && position < this.columns.length) {
      this.columns[position].refresh();
    } else if (position === null || position === undefined) {
      this.currentColumn.refresh();
    }
  }

  /**
   * Get the index of current column
   */
  public getCurrentIndex(): number {
    return this.columns.indexOf(this.currentColumn);
  }

  /**
   * Sort columns content
   * @param indice
   */
  public sort(indice: 1 | -1 = 1): void {
    for (const column of this.columns) {
      column.sort(indice);
    }
  }

  /**
   * Collapse/deploy all the columns
   * @param collapse
   */
  public collapseAll(collapse: boolean) {
    this.isCollapsed = collapse;
    this.columns.forEach((c) => c.collapse(collapse));
  }

  /**
   * Collapse/deploy desired column
   * @param id
   * @param collapse
   */
  public collapseById(id: number, collapse: boolean) {
    if (id > -1 && id < this.columns.length) {
      this.columns[id].collapse(collapse);
    }
  }
}
